<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("shopee_promotion.menu_title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-md-4">
                            <label class="form-control-label">{{ $t("shopee_promotion.shop") }}</label>
                            <el-select
                                v-model="shopId"
                                class="w-100"
                                :placeholder="$t('shopee_promotion.shop_placeholder')"
                                :no-data-text="$t('shopee_promotion.no_shop')"
                                @change="onShopChange"
                            >
                                <el-option
                                    v-for="shop in shopeeShops"
                                    :key="shop.id"
                                    :label="shopLabel(shop)"
                                    :value="String(shop.shop_id)"
                                ></el-option>
                            </el-select>
                            <p class="mb-0 mt-1 small text-muted">
                                {{ $t("shopee_promotion.shop_on_shopee") }}: {{ shopNameText }}
                            </p>
                        </div>
                        <div class="col-md-8">
                            <p class="mb-0 small">
                                <span class="text-muted">{{ $t("shopee_promotion.last_run") }}:</span>
                                <span v-if="lastRun">{{ lastRunText }}</span>
                                <span v-else>{{ $t("shopee_promotion.last_run_never") }}</span>
                            </p>
                            <p class="mb-0 small">
                                <span class="text-muted">{{ $t("shopee_promotion.fetched_at") }}:</span>
                                {{ fetchedAtText }}
                            </p>
                            <p class="mb-0 text-muted small">{{ $t("shopee_promotion.hint") }}</p>
                            <!-- §10.3: the setting lives on /app-catalog/storefront; this
                                 page only reports what it is set to, so an operator who
                                 wonders why an upcoming promotion is invisible on the
                                 storefront is told where the switch is. -->
                            <p class="mb-0 small">
                                <span class="text-muted">{{ $t("shopee_promotion.show_upcoming_state") }}:</span>
                                {{ showUpcoming ? $t("shopee_promotion.show_upcoming_on") : $t("shopee_promotion.show_upcoming_off") }}
                            </p>
                        </div>
                    </div>
                </template>

                <!-- "ปิดอยู่": the master flag is down (503 / enabled:false). Its own
                     panel rather than an error toast — a switch that is deliberately
                     off is not a failure, and §9 step 2 rolls with it off on purpose. -->
                <div class="px-4 pb-3" v-if="disabled">
                    <div class="alert alert-secondary py-2 px-3 mb-0">
                        <i class="fa fa-power-off mr-1"></i>
                        {{ $t("shopee_promotion.disabled_state") }}
                    </div>
                </div>

                <template v-else>
                    <!-- shadow mode writes NOTHING (§9 step 3). Said out loud, because
                         "sync เดี๋ยวนี้" otherwise looks like it failed silently. -->
                    <div class="px-4 pb-2" v-if="isShadowMode">
                        <div class="alert alert-info py-2 px-3 mb-0">
                            <i class="fa fa-eye mr-1"></i>
                            {{ $t("shopee_promotion.mode_shadow_hint") }}
                        </div>
                    </div>

                    <!-- §10.5: promotions are mirrored from the ONE master shop. A
                         different shop would answer 422 shop_not_master, so the buttons
                         are disabled and the reason is on screen instead. -->
                    <div class="px-4 pb-2" v-if="notMasterShop">
                        <div class="alert alert-warning py-2 px-3 mb-0">
                            <i class="fa fa-exclamation-triangle mr-1"></i>
                            {{ $t("shopee_promotion.err_shop_not_master") }}
                            <span v-if="masterShopId"> ({{ masterShopId }})</span>
                        </div>
                    </div>

                    <div class="px-4 pb-3 d-flex flex-wrap align-items-center">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-primary mr-2"
                            :disabled="!canRun"
                            @click="run(true)"
                        >
                            <i class="fa fa-spinner fa-spin mr-1" v-if="dryRunning"></i>
                            {{ $t("shopee_promotion.btn_dry_run") }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-primary mr-2"
                            :disabled="!canRun"
                            @click="confirmSync()"
                        >
                            <i class="fa fa-spinner fa-spin mr-1" v-if="syncing"></i>
                            {{ $t("shopee_promotion.btn_sync_now") }}
                        </button>
                        <span class="text-muted small align-self-center mr-3">
                            {{ $t("shopee_promotion.btn_dry_run_hint") }}
                        </span>
                        <span class="text-muted small align-self-center" v-if="selected.length">
                            {{ $t("shopee_promotion.selected_count", { count: selected.length }) }}
                        </span>
                        <span class="text-muted small align-self-center" v-else>
                            {{ $t("shopee_promotion.selected_none") }}
                        </span>
                    </div>

                    <div class="px-4 pb-3" v-if="running">
                        <div class="alert alert-info py-2 px-3 mb-0">
                            <i class="fa fa-spinner fa-spin mr-1"></i>
                            {{ $t("shopee_promotion.running") }}
                        </div>
                    </div>

                    <div class="px-4 pb-3" v-if="errorKey">
                        <div class="alert alert-warning py-2 px-3 mb-0">
                            <i class="fa fa-exclamation-triangle mr-1"></i>
                            {{ $t(errorKey) }}
                            <span class="d-block small" v-if="errorDetail">{{ errorDetail }}</span>
                        </div>
                    </div>

                    <!-- The run summary, including a dry run: §6 wants the operator to
                         read what WOULD happen before pressing the primary button. -->
                    <div class="px-4 pb-3" v-if="runSummary">
                        <div class="alert py-2 px-3 mb-0" :class="runSummary.dryRun ? 'alert-secondary' : 'alert-success'">
                            <strong v-if="runSummary.dryRun">{{ $t("shopee_promotion.result_dry_run") }}</strong>
                            <strong v-else>{{ $t("shopee_promotion.result_applied") }}</strong>
                            <span class="d-block small">{{ runSummaryText }}</span>
                        </div>
                    </div>

                    <div v-loading="loading">
                        <el-table
                            :data="promotions"
                            :row-key="rowKey"
                            size="small"
                            header-row-class-name="thead-light"
                            :empty-text="$t('shopee_promotion.empty')"
                            @selection-change="onSelectionChange"
                        >
                            <!-- reserve-selection keeps the ticks when the table data is
                                 replaced by a reload, which is what happens after a run. -->
                            <el-table-column
                                type="selection"
                                width="45"
                                reserve-selection
                                :selectable="isSelectable"
                            ></el-table-column>
                            <el-table-column :label="$t('shopee_promotion.col_type')" width="120">
                                <template slot-scope="{ row }">
                                    <el-tag size="mini" :type="row.promotion_type === 'flash_sale' ? 'danger' : 'info'">
                                        {{ $t(typeKey(row)) }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('shopee_promotion.col_name')" min-width="220">
                                <template slot-scope="{ row }">
                                    {{ row.name || "-" }}
                                    <span class="d-block text-muted small text-monospace">
                                        {{ row.promotion_id }}
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('shopee_promotion.col_window')" min-width="190">
                                <template slot-scope="{ row }">
                                    {{ windowText(row) }}
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('shopee_promotion.col_status')" width="120">
                                <template slot-scope="{ row }">
                                    <el-tag size="mini" :type="statusBadge(row).type">
                                        {{ $t(statusBadge(row).i18nKey) }}
                                    </el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column
                                :label="$t('shopee_promotion.col_item_count')"
                                width="110"
                                align="right"
                                prop="item_count"
                            ></el-table-column>
                            <el-table-column :label="$t('shopee_promotion.col_app_state')" min-width="160">
                                <template slot-scope="{ row }">
                                    <span>{{ $t(appStateKey(row), { count: skuCount(row) }) }}</span>
                                    <span class="d-block text-muted small" v-if="lastSyncedText(row)">
                                        {{ lastSyncedText(row) }}
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column :label="$t('actions')" width="130" align="right">
                                <template slot-scope="{ row }">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-secondary"
                                        :disabled="!hasResult(row)"
                                        :title="hasResult(row) ? '' : $t('shopee_promotion.btn_view_result_hint')"
                                        @click="openResult(row)"
                                    >
                                        {{ $t("shopee_promotion.btn_view_result") }}
                                    </button>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </template>
            </card>
        </div>

        <!-- Per-SKU result. el-dialog, not el-drawer: element-ui 2.4.11 has no
             drawer component (the same reason ShopeeCategoryMap.vue uses swal for
             its confirmations). -->
        <el-dialog
            :title="$t('shopee_promotion.result_title')"
            :visible.sync="resultVisible"
            width="820px"
            append-to-body
        >
            <div v-if="resultRow">
                <p class="mb-1">
                    <strong>{{ resultRow.name || resultRow.promotion_id }}</strong>
                    <el-tag size="mini" class="ml-2" :type="statusBadge(resultRow).type">
                        {{ $t(statusBadge(resultRow).i18nKey) }}
                    </el-tag>
                </p>
                <p class="text-muted small mb-3">
                    {{ windowText(resultRow) }} ·
                    {{ $t(promoActionKey(resultDetail)) }}
                </p>

                <el-table
                    :data="resultSkus"
                    size="mini"
                    header-row-class-name="thead-light"
                    :empty-text="$t('shopee_promotion.result_empty')"
                >
                    <el-table-column :label="$t('shopee_promotion.col_sku')" min-width="200">
                        <template slot-scope="{ row }">
                            {{ row.sku_code || "-" }}
                            <span class="d-block text-muted small text-monospace" v-if="row.model_id || row.item_id">
                                {{ row.model_id || row.item_id }}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column
                        :label="$t('shopee_promotion.col_price_before')"
                        width="120"
                        align="right"
                    >
                        <template slot-scope="{ row }">
                            {{ priceText(row.prev_price) }}
                        </template>
                    </el-table-column>
                    <el-table-column
                        :label="$t('shopee_promotion.col_price_after')"
                        width="120"
                        align="right"
                    >
                        <template slot-scope="{ row }">
                            {{ priceText(row.price) }}
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_promotion.col_action')" width="110">
                        <template slot-scope="{ row }">
                            <el-tag size="mini" :type="skuActionTagType(row.action)">
                                {{ $t(skuActionKey(row)) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('shopee_promotion.col_reason')" min-width="240">
                        <template slot-scope="{ row }">
                            <span v-if="row.reason_code">{{ $t(reasonKey(row)) }}</span>
                            <span v-else class="text-muted">-</span>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- The same warning shape the to-seller preview renders
                     (shopeeToSeller#toSellerWarnings): {code, sku_code, item_id,
                     model_id, message}. -->
                <div class="mt-3" v-if="resultWarnings.length">
                    <h6 class="text-uppercase text-muted mb-2">
                        {{ $t("shopee_promotion.warnings_title") }}
                    </h6>
                    <ul class="mb-0 pl-3 small">
                        <li v-for="(warn, idx) in resultWarnings" :key="idx">
                            {{ $t(reasonKey(warn)) }}
                            <span class="text-muted" v-if="warn.sku_code"> · {{ warn.sku_code }}</span>
                            <span class="text-muted" v-else-if="warn.model_id"> · {{ warn.model_id }}</span>
                            <span class="text-muted" v-else-if="warn.item_id"> · {{ warn.item_id }}</span>
                            <span class="d-block text-muted" v-if="warn.message">{{ warn.message }}</span>
                        </li>
                    </ul>
                </div>
            </div>
            <span slot="footer" class="dialog-footer">
                <button type="button" class="btn btn-sm btn-secondary" @click="resultVisible = false">
                    {{ $t("shopee_promotion.btn_close") }}
                </button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
// B7 FE-1 — "โปรโมชั่น Shopee → seller" (spec
// docs/specs/2026-10-02-shopee-promotions-to-seller-design.md §6, endpoints §5.1).
//
// WHAT THIS PAGE IS FOR: Shopee's own Discounts and Shop Flash Sales are
// mirrored into the app's per-SKU flash window, so a promotion the owner set up
// on Shopee shows the SAME price on the storefront without anybody typing it
// twice. This screen is where an operator reads what Shopee currently has, what
// this feature did about it, and — with ตรวจสอบ (dry-run) — what it WOULD do.
//
// WHY A SHOP-LEVEL PAGE and not a button inside ShopeeToSellerModal: a promotion
// spans many listings, so it is store-level work. Same reasoning, and the same
// menu group (ตั้งค่าร้านค้า), as ShopeeCategoryMap.vue.
//
// THREE THINGS THIS PAGE DELIBERATELY DOES NOT DO:
//   * it never edits a promotion — the mirror is one-directional (§1), so there
//     is no form here, only buttons that re-read Shopee;
//   * it never decides a price. Every number on screen came from the run
//     response; the winner rule and the ownership rule are BE-1's (§4.5/§4.6);
//   * it does not poll the LIST. Only `runs` is polled (every 3 s, §6), because
//     the list costs the shop two Shopee calls and the runs endpoint costs none.
//
// ALL THE LOGIC IS IN @/utils/shopeePromotion — this repo's jest cannot mount a
// .vue file, so the sync body, every badge, every sentence and the Bangkok clock
// live there and are asserted by tests/unit/shopeePromotion.spec.js.
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import { Select, Option, Table, TableColumn, Tag, Dialog, Loading } from "element-ui";
import swal from "sweetalert2";
import { ShopeeService } from "@/resource";
import { SHOPEE_PLATFORM_ID } from "@/utils/shopeeToSeller";
import {
    PROMOTION_RUNS_POLL_INTERVAL_MS,
    appStateI18nKey,
    appStateSkuCount,
    buildSyncBody,
    formatDateTime,
    formatWindow,
    inFlightRunId,
    isPromotionDisabled,
    latestRun,
    listParams,
    promoShowUpcoming,
    promoSkuActionI18nKey,
    promoSkuReasonI18nKey,
    promotionActionI18nKey,
    promotionErrorDetail,
    promotionErrorI18nKey,
    promotionInFlightRunId,
    promotionKey,
    promotionStatusBadge,
    promotionTypeI18nKey,
    promotionWarnings,
    runsParams,
    summarizeRun
} from "@/utils/shopeePromotion";

export default {
    name: "AppCatalogShopeePromotions",
    components: {
        // Element UI is NOT registered globally in this app (only Input, Tooltip
        // and Popover are — src/plugins/globalComponents.js), so every el-* used
        // above has to be registered here.
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Dialog.name]: Dialog
    },
    directives: {
        // v-loading is a DIRECTIVE and is not installed globally either.
        loading: Loading.directive
    },
    data() {
        return {
            shopId: "",
            loading: false,
            dryRunning: false,
            syncing: false,
            // The run this page is watching because a 409 said one was already in
            // flight (the bulkInFlightJobId move, §6). 0 = none.
            attachedRunId: 0,
            pollTimer: null,
            errorKey: "",
            errorDetail: "",
            disabled: false,
            mode: "",
            masterShopId: "",
            showUpcoming: false,
            fetchedAt: "",
            promotions: [],
            selected: [],
            lastRun: null,
            // The last run THIS page performed, kept apart from `lastRun` (which
            // is whatever the runs endpoint reports, including a ticker's): the
            // per-SKU drawer can only show results the current response carries,
            // because §5.1 keeps them in the response, not in the runs rows.
            runResult: null,
            resultVisible: false,
            resultRow: null,
            shopInfo: null
        };
    },
    computed: {
        ...mapGetters({
            sellers: "Platform/getSellers"
        }),
        // Shopee sellers only — the same platform_id === 3 filter
        // /admin/product_platform and ShopeeCategoryMap use for their shop selects.
        shopeeShops() {
            const list = this.sellers || [];
            return list.filter(function(shop) {
                return Number(shop.platform_id) === SHOPEE_PLATFORM_ID;
            });
        },
        shopNameText() {
            const info = this.shopInfo || {};
            return info.shop_name || "-";
        },
        fetchedAtText() {
            return formatDateTime(this.fetchedAt) || this.$t("shopee_promotion.fetched_at_never");
        },
        lastRunText() {
            const summary = summarizeRun(this.lastRun);
            return this.$t("shopee_promotion.last_run_line", {
                at: summary.at || "-",
                promotions: summary.promotionsApplied,
                set: summary.skusSet,
                cleared: summary.skusCleared
            });
        },
        runSummary() {
            if (!this.runResult) return null;
            const summary = summarizeRun(this.runResult);
            summary.dryRun = this.runResult.dry_run === true;
            return summary;
        },
        runSummaryText() {
            const s = this.runSummary;
            if (!s) return "";
            return this.$t("shopee_promotion.result_summary", {
                total: s.promotionsTotal,
                applied: s.promotionsApplied,
                set: s.skusSet,
                cleared: s.skusCleared,
                skipped: s.skusSkipped
            });
        },
        isShadowMode() {
            return String(this.mode || "").toLowerCase() === "shadow";
        },
        // §10.5: only the master shop is mirrored. An empty master_shop means the
        // BE has not pinned one (config left blank), which is NOT a block — the
        // endpoint would accept any shop of this enterprise then.
        notMasterShop() {
            if (this.masterShopId === "" || this.shopId === "") return false;
            return String(this.masterShopId) !== String(this.shopId);
        },
        running() {
            return this.attachedRunId > 0 || this.dryRunning || this.syncing;
        },
        canRun() {
            if (this.disabled || this.notMasterShop) return false;
            if (this.shopId === "") return false;
            return !this.running;
        },
        resultDetail() {
            const row = this.resultRow;
            if (!row) return null;
            const key = promotionKey(row);
            const results = (this.runResult && this.runResult.results) || [];
            for (let i = 0; i < results.length; i++) {
                if (promotionKey(results[i]) === key) return results[i];
            }
            return null;
        },
        resultSkus() {
            const detail = this.resultDetail;
            const list = (detail && detail.skus) || [];
            return Array.isArray(list) ? list.filter((s) => s && typeof s === "object") : [];
        },
        resultWarnings() {
            return promotionWarnings(this.resultDetail);
        }
    },
    mounted() {
        const self = this;
        this.listSeller().then(function() {
            self.selectFirstShop();
        });
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        ...mapActions({
            listSeller: "Platform/listSeller"
        }),
        shopLabel(shop) {
            const name = (shop && shop.seller_name) || "";
            const id = (shop && shop.shop_id) || "";
            return name ? name + " (" + id + ")" : String(id);
        },
        selectFirstShop() {
            const shops = this.shopeeShops;
            if (!shops.length) {
                this.shopId = "";
                return;
            }
            this.shopId = String(shops[0].shop_id);
            this.onShopChange();
        },
        onShopChange() {
            this.stopPolling();
            this.attachedRunId = 0;
            this.runResult = null;
            this.selected = [];
            this.clearMessages();
            // runs FIRST: it answers `enabled` and `master_shop`, which decide
            // whether the list is worth asking for at all (a disabled feature
            // answers 503 on the list, and showing that as an error would read as
            // a breakage rather than as a switch that is down).
            this.loadRuns().then(() => {
                if (this.disabled) {
                    this.promotions = [];
                    return;
                }
                this.reload();
            });
            this.loadShopInfo();
        },
        clearMessages() {
            this.errorKey = "";
            this.errorDetail = "";
        },
        rowKey(row) {
            return promotionKey(row);
        },
        typeKey(row) {
            return promotionTypeI18nKey(row);
        },
        statusBadge(row) {
            return promotionStatusBadge(row);
        },
        windowText(row) {
            const text = formatWindow(row && row.starts_at, row && row.ends_at);
            return text === "" ? "-" : text;
        },
        appStateKey(row) {
            return appStateI18nKey(row);
        },
        skuCount(row) {
            return appStateSkuCount(row);
        },
        lastSyncedText(row) {
            const link = (row && row.link) || null;
            if (!link) return "";
            const at = formatDateTime(link.last_synced_at) || formatDateTime(link.ended_at);
            return at === "" ? "" : this.$t("shopee_promotion.link_at", { at: at });
        },
        skuActionKey(row) {
            return promoSkuActionI18nKey(row);
        },
        promoActionKey(detail) {
            return promotionActionI18nKey(detail);
        },
        reasonKey(row) {
            return promoSkuReasonI18nKey(row);
        },
        skuActionTagType(action) {
            const a = String(action || "").toLowerCase();
            if (a === "set") return "success";
            if (a === "clear") return "warning";
            if (a === "skip") return "info";
            return "";
        },
        priceText(value) {
            if (value === null || value === undefined || value === "") return "-";
            const n = Number(value);
            if (!isFinite(n)) return "-";
            return n.toFixed(2);
        },
        // Only a promotion the CURRENT run reported has per-SKU rows to show
        // (§5.1 keeps them in the response). A row with no result keeps the
        // button disabled rather than opening an empty drawer.
        hasResult(row) {
            const key = promotionKey(row);
            const results = (this.runResult && this.runResult.results) || [];
            for (let i = 0; i < results.length; i++) {
                if (promotionKey(results[i]) === key) return true;
            }
            return false;
        },
        isSelectable(row) {
            // An unsupported promotion (source=1 / add-on / voucher, §7 #8) is
            // never mirrored, so ticking it would only produce a skip row.
            return !!row && row.status !== "unsupported";
        },
        onSelectionChange(rows) {
            this.selected = Array.isArray(rows) ? rows : [];
        },
        openResult(row) {
            this.resultRow = row;
            this.resultVisible = true;
        },
        async loadShopInfo() {
            const shopId = this.shopId;
            this.shopInfo = null;
            if (shopId === "") return;
            try {
                const res = await ShopeeService.ShopInfo(shopId);
                // The operator may have switched shops while this was in flight;
                // a late answer must not name the wrong shop.
                if (this.shopId !== shopId) return;
                this.shopInfo = (res && res.data) || null;
            } catch (err) {
                // DECORATION, NOT DATA: the header shows "-" and the page keeps
                // working (platform_shopee.js#ShopInfo says so in as many words).
                if (this.shopId === shopId) this.shopInfo = null;
            }
        },
        async loadRuns() {
            if (this.shopId === "") {
                this.lastRun = null;
                return;
            }
            try {
                const res = await ShopeeService.PromotionRuns(runsParams(this.shopId));
                const data = (res && res.data) || {};
                this.disabled = isPromotionDisabled(data);
                this.mode = data.mode || "";
                this.masterShopId = data.master_shop === null || data.master_shop === undefined
                    ? ""
                    : String(data.master_shop);
                this.showUpcoming = promoShowUpcoming(data);
                this.lastRun = latestRun(data);
                const inflight = inFlightRunId(data);
                if (inflight !== null) {
                    // Somebody else (or the ticker) is running: attach and poll
                    // instead of offering a button that would answer 409.
                    this.attachedRunId = inflight;
                    this.startPolling();
                } else if (this.attachedRunId > 0) {
                    // The run we were watching has finished — stop, and re-read the
                    // table so the "สถานะในแอป" column reflects what it did.
                    this.attachedRunId = 0;
                    this.stopPolling();
                    this.reload();
                }
            } catch (err) {
                this.disabled = isPromotionDisabled(err);
                if (!this.disabled) {
                    this.errorKey = promotionErrorI18nKey(err);
                    this.errorDetail = promotionErrorDetail(err);
                }
            }
        },
        async reload() {
            if (this.shopId === "") {
                this.promotions = [];
                this.fetchedAt = "";
                return;
            }
            this.loading = true;
            try {
                const res = await ShopeeService.PromotionList(listParams(this.shopId, {}));
                const data = (res && res.data) || {};
                const list = Array.isArray(data.promotions) ? data.promotions : [];
                this.promotions = list.filter((p) => p && typeof p === "object");
                this.fetchedAt = data.fetched_at || "";
                if (data.mode) this.mode = data.mode;
                this.showUpcoming = promoShowUpcoming(data) || this.showUpcoming;
            } catch (err) {
                this.promotions = [];
                this.disabled = isPromotionDisabled(err);
                if (!this.disabled) {
                    this.errorKey = promotionErrorI18nKey(err);
                    this.errorDetail = promotionErrorDetail(err);
                    this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
                }
            } finally {
                this.loading = false;
            }
        },
        confirmSync() {
            const self = this;
            // element-ui 2.4.11 has no MessageBox in this app; sweetalert2 is the
            // dialog every other Admin screen uses.
            swal.fire({
                title: this.$t("shopee_promotion.confirm_title"),
                text: this.$t("shopee_promotion.confirm_text", {
                    count: this.selected.length
                }),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("shopee_promotion.btn_sync_now"),
                cancelButtonText: this.$t("shopee_promotion.btn_cancel"),
                confirmButtonClass: "btn btn-primary btn-fill",
                cancelButtonClass: "btn btn-secondary btn-fill",
                buttonsStyling: false
            }).then(function(result) {
                if (result.value) self.run(false);
            });
        },
        async run(dryRun) {
            if (!this.canRun) return;
            this.clearMessages();
            if (dryRun) {
                this.dryRunning = true;
            } else {
                this.syncing = true;
            }
            try {
                const body = buildSyncBody({
                    shopId: this.shopId,
                    dryRun: dryRun,
                    selected: this.selected
                });
                const res = await ShopeeService.PromotionSync(body);
                this.runResult = (res && res.data) || null;
                this.$notify({
                    type: "success",
                    message: this.$t(
                        dryRun ? "shopee_promotion.result_dry_run" : "shopee_promotion.result_applied"
                    )
                });
                await this.loadRuns();
                // A real run changed the per-SKU state the table's "สถานะในแอป"
                // column reads, so the list is re-read; a dry run changed nothing
                // and must not spend two more Shopee calls.
                if (!dryRun) await this.reload();
            } catch (err) {
                const inflight = promotionInFlightRunId(err);
                if (inflight !== null) {
                    // 409: not an error — attach to the run that is already going
                    // and poll it (the bulkInFlightJobId move of §6).
                    this.attachedRunId = inflight;
                    this.errorKey = "shopee_promotion.err_in_flight";
                    this.startPolling();
                } else {
                    this.disabled = isPromotionDisabled(err);
                    this.errorKey = promotionErrorI18nKey(err);
                    this.errorDetail = promotionErrorDetail(err);
                    // §5.1: a 502 still carries the PARTIAL result, and an
                    // operator told only "failed" presses the button again.
                    const data = (err && err.response && err.response.data) || {};
                    if (data.result && typeof data.result === "object") this.runResult = data.result;
                }
            } finally {
                this.dryRunning = false;
                this.syncing = false;
            }
        },
        startPolling() {
            if (this.pollTimer !== null) return;
            const self = this;
            this.pollTimer = setInterval(function() {
                self.loadRuns();
            }, PROMOTION_RUNS_POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollTimer === null) return;
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }
};
</script>
